import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  array,
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOfType,
  shape,
  string,
} from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import { URL_API_LISTINGS_SELL_SHEET } from "../../constants";
import {
  api,
  displayMeter,
  formatNumber,
  formatTitleCase,
  getApiErrorMessage,
  scrollIfNeeded,
  validateInput,
} from "../../helpers";
import { colors } from "../../styles/Styles.variables";
import { selectCachedAdditionalInfo } from "../additional-info/AdditionalInfo.redux";
import { actions } from "./ModalSellSheet.redux";
import { actions as selectFilesActions } from "../select-files/SelectFiles.redux";

import Alert from "../../styles/components/Alert";
import Button from "../../styles/components/Button";
import Checkbox from "../../styles/components/Checkbox";
import Form from "../form";
import Group from "../form/components/Group";
import Label from "../form/components/Label";
import Modal from "../modal";
import ModalFilesPhotos from "../modal-files/components/ModalFiles.photos";
import ModalFilesReports from "../modal-files/components/ModalFiles.reports";
import Row from "../form/components/Row";
import Switch from "../form/components/Switch";
import Text from "../form/components/Text";
import TextArea from "../form/components/TextArea";
import WysiwygEditor from "../form/components/WysiwygEditor";

const initData = {
  action: "create",
  branding: true,
  customPrice: "",
  hash: null,
  note: "",
  recipients: "",
  serviceNotes: false,
  servicePrice: false,
};

const initErrors = {
  customPrice: null,
  note: null,
  recipients: null,
};

/**
 * Modal form for emailing selected listing files to a customer
 *
 * @param {array}    props.categories
 * @param {function} props.close
 * @param {array}    props.files
 * @param {object}   props.listing
 * @param {object}   props.location
 * @param {bool}     props.open
 * @param {string}   props.targetDevice
 * @param {function} props.trackEvent
 * @param {function} props.trackModalView
 * @param {function} props.updateUser
 * @param {object}   props.user
 * @returns JSX
 * @public
 */
const ModalSellSheet = ({
  categories = [],
  close,
  files = [],
  listing,
  location,
  open = false,
  targetDevice,
  trackEvent,
  trackModalView,
  updateUser,
  user,
}) => {
  const {
    id: userId,
    email = "",
    first_name = "",
    last_name = "",
    phone = "",
  } = user || {};

  const equipmentId = listing?.equipmentId;

  const { serviceHistory: serviceHistoryInfo = [] } =
    useSelector(state =>
      selectCachedAdditionalInfo(state, "additionalInfoService", equipmentId)
    ) || {};
  const hasServiceHistory = !!serviceHistoryInfo.length;

  const [action, setAction] = useState("create");
  const [data, setData] = useState(() => ({
    ...initData,
    email,
    first_name,
    last_name,
    serviceHistory: hasServiceHistory,
    phone,
  }));
  const [errors, setErrors] = useState(() => ({
    ...initErrors,
    email: null,
    first_name: null,
    last_name: null,
    phone: null,
  }));
  const [inProgress, setInProgress] = useState(false);
  const [method, setMethod] = useState("post");
  const [sellSheetId, setSellSheetId] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setData(prevData => ({
      ...prevData,
      serviceHistory: hasServiceHistory,
      serviceNotes: hasServiceHistory && !!prevData.serviceNotes,
      servicePrice: hasServiceHistory && !!prevData.servicePrice,
    }));
  }, [hasServiceHistory]);

  // Open email product sheet form modal
  const dispatch = useDispatch();
  const handleModalSellSheet = useCallback(
    (forceOpen = false) => {
      dispatch(actions.handle(forceOpen));
    },
    [dispatch]
  );
  const handleSavedSelections = useCallback(
    (attachments = [], photos = []) => {
      dispatch(selectFilesActions.setChosenSavedState({ attachments, photos }));
    },
    [dispatch]
  );

  const { state: locationState = {} } = location || {};

  useEffect(() => {
    const {
      action = "create",
      attachments,
      email: recipients = "",
      hash,
      hideBranding = false,
      id,
      includeServiceHistory,
      note = "",
      photos,
      price,
      showServiceNotes = false,
      showServicePrice = false,
      user_id: lsUserId,
    } = locationState;

    if (`${lsUserId}` === `${userId}` && !!hash) {
      setAction(action);

      setData(prevData => ({
        ...prevData,
        branding: !hideBranding,
        customPrice: !!price ? `$${formatNumber(price)}` : "",
        note,
        recipients: action === "edit" ? recipients : prevData.recipients,
        serviceHistory: hasServiceHistory && !!includeServiceHistory,
        serviceNotes:
          hasServiceHistory && !!includeServiceHistory && !!showServiceNotes,
        servicePrice:
          hasServiceHistory && !!includeServiceHistory && !!showServicePrice,
      }));

      if (action === "edit") {
        setMethod("put");
      }

      if (!!id || id === 0) {
        setSellSheetId(id);
      }

      handleSavedSelections(attachments, photos);
      handleModalSellSheet(true);
    }
  }, [
    locationState,
    handleModalSellSheet,
    handleSavedSelections,
    hasServiceHistory,
    userId,
  ]);

  const header = useRef();
  const form = useRef();
  const isOpen = useRef(false);

  // Stack form columns for mobile
  const shouldStack = useMemo(() => targetDevice === "mobile", [targetDevice]);

  // Group selected files for display
  const {
    attachmentIds,
    photos,
    photoHandles,
    reportsPublic,
    reportsInternal,
  } = useMemo(() => {
    const grouped = files.reduce(
      (grouped, file) => {
        if (file.fileType === "photo") {
          grouped.photos.push(file);
          grouped.photoHandles.push(file.fileId);
        } else if (file.fileIsPublic) {
          grouped.attachmentIds.push(file.fileId);
          grouped.reportsPublic.push(file);
        } else {
          grouped.attachmentIds.push(file.fileId);
          grouped.reportsInternal.push(file);
        }

        return grouped;
      },
      {
        attachmentIds: [],
        photos: [],
        photoHandles: [],
        reportsPublic: [],
        reportsInternal: [],
      }
    );

    return grouped;
  }, [files]);

  /**
   * Handle form error
   * @param {string} message
   * @public
   */
  const handleFormError = message => {
    setInProgress(false);
    setServerError(message);
    scrollIfNeeded(header.current);
  };

  /**
   * Handle API error
   * @param {*} error
   * @public
   */
  const handleApiError = error => {
    handleFormError(getApiErrorMessage(error));
  };

  /**
   * Handle branding switch
   * @public
   */
  const handleBranding = () => {
    setData(prevData => ({
      ...prevData,
      branding: !prevData.branding,
    }));
  };

  /**
   * Handle form input
   * @param {event} event
   * @public
   */
  const handleInput = event => {
    const { target: input } = event;
    const field = input.id;
    const value = !!input ? input.value : null;
    let error = validateInput(input);

    if (field === "recipients") {
      if (
        !!value &&
        !/^[^\s@,]+@[^\s@,]+\.[^\s@,]+(, ?[^\s@,]+@[^\s@,]+\.[^\s@,]+)*$/.test(
          value
        )
      ) {
        error = "Enter a comma-separated list of valid email addresses";
      } else {
        error = error || null;
      }
    }

    if (!!field) {
      setData(prevData => ({ ...prevData, [field]: value }));
      setErrors(prevErrors => ({ ...prevErrors, [field]: error }));
    }
  };

  /**
   * Handle service history checkbox
   * @public
   */
  const handleServiceHistory = () => {
    setData(prevData => ({
      ...prevData,
      serviceHistory: !prevData.serviceHistory,
    }));
  };

  /**
   * Handle service notes checkbox
   * @public
   */
  const handleServiceNotes = () => {
    setData(prevData => ({
      ...prevData,
      serviceNotes: !prevData.serviceNotes,
    }));
  };

  /**
   * Handle service price checkbox
   * @public
   */
  const handleServicePrice = () => {
    setData(prevData => ({
      ...prevData,
      servicePrice: !prevData.servicePrice,
    }));
  };

  /**
   * Handle form submission
   * @param {event} event
   * @public
   */
  const handleSubmit = event => {
    event.preventDefault();

    if (!form.current) {
      return false;
    }

    if (!form.current.checkValidity() || !!errors.recipients) {
      [...form.current.elements].forEach(input => {
        if (data.hasOwnProperty(input.id) && input.id !== "branding") {
          handleInput({ target: input });
        }
      });

      handleFormError("Please check form for errors");
      return;
    } else {
      postData();
    }
  };

  /**
   * Post data to API
   * @public
   */
  const postData = () => {
    setInProgress(true);

    const price = !!data.customPrice
      ? parseFloat(data.customPrice.replace(/[$,]/g, ""))
      : data.customPrice;
    const formattedPrice = !!price ? `$${formatNumber(price)}` : null;

    const dataToPost = {
      attachments: attachmentIds,
      email: data.recipients.replace(/ /g, ""),
      hideBranding: !data.branding,
      includeServiceHistory: data.serviceHistory,
      isEdit: action === "edit",
      listingFormatted: {
        advertisedPrice: !!listing.advertisedPrice
          ? `$${formatNumber(listing.advertisedPrice)}`
          : null,
        categories: !!categories.length ? categories.join(", ") : null,
        customPrice: formattedPrice,
        location: `${listing.locationCity}, ${listing.locationState} ${listing.locationZip}`,
        make: listing.make,
        meter: displayMeter(listing)
          ? `${formatNumber(listing.meterValue)} ${listing.meterType}`
          : "–",
        model: listing.model,
        salePrice: !!listing.salePrice
          ? `$${formatNumber(listing.salePrice)}`
          : null,
        year: !!listing.year ? listing.year : "",
      },
      note: data.note,
      price,
      photos: photoHandles,
      salesperson: {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: !!data.phone
          ? data.phone.replace(/.*(\d{3}).*(\d{3}).*(\d{4})(.*)/, "$1-$2-$3$4")
          : null,
      },
      showBranding: data.branding,
      showServiceNotes: data.serviceNotes,
      showServicePrice: data.servicePrice,
    };

    const url = `${URL_API_LISTINGS_SELL_SHEET}/${
      !!sellSheetId || sellSheetId === 0 ? sellSheetId : listing.equipmentId
    }`;

    api({ method, url, data: dataToPost })
      .then(response => {
        if (!!response.data && !!response.data.success) {
          if (!!trackEvent) {
            trackEvent({
              category: "Form",
              action: "Submit",
              label: "Send Files Modal",
            });
          }

          setInProgress(false);
          setServerError(null);
          setSuccess(true);
          updateUser(dataToPost.salesperson);
          scrollIfNeeded(header.current);
        } else {
          handleApiError(response);
        }
      })
      .catch(handleApiError);
  };

  // Respond to modal open/close
  useEffect(() => {
    if (!isOpen.current && open && !!trackModalView) {
      isOpen.current = open;
      trackModalView("sendfiles");
    }

    if (isOpen.current && !open) {
      setData(prevData => ({ ...prevData, ...initData }));
      setErrors(prevErrors => ({ ...prevErrors, ...initErrors }));
      setInProgress(false);
      setServerError(null);
      setSuccess(false);
    }
  }, [open, trackEvent, trackModalView]);

  return (
    <Modal aria-label="Product Sheet" close={close} open={open}>
      <header ref={header}>
        <h1>
          <strong>
            {action !== "create" && formatTitleCase(action)}{" "}
            {action !== "create" ? "Custom" : "Customize"} Product Sheet
          </strong>
        </h1>
        {action === "edit" ? (
          <p>
            You are editing a previous Product Sheet. Any changed information,
            such as the pricing or meter, will automatically be updated on the
            Product Sheet.
          </p>
        ) : (
          <p>
            A link to download the Product Sheet will be automatically added to
            the email along with your message. We will also send you a copy of
            the message, along with the ability to Edit, Resend and Clone the
            message.
          </p>
        )}

        {serverError && <Alert type="error">{serverError}</Alert>}
        {success && (
          <Alert type="success">Thanks! Your email is on its way.</Alert>
        )}
      </header>
      {!success && (
        <Form onSubmit={handleSubmit} formRef={form}>
          <div className="form-fields">
            <ModalFilesPhotos photos={photos} targetDevice={targetDevice} />
            <ModalFilesReports
              label="Public-facing Reports"
              reports={reportsPublic}
            />
            <ModalFilesReports
              label="Internal-only Reports"
              reports={reportsInternal}
            />
            <Row>
              <Group
                className={targetDevice === "desktop" ? "w50" : null}
                error={errors.customPrice}
              >
                <Label htmlFor="customPrice">
                  Custom Price <small>(if applicable)</small>
                </Label>
                <Text
                  id="customPrice"
                  maxLength="20"
                  onChange={handleInput}
                  value={data.customPrice}
                />
              </Group>
            </Row>
            <Group error={errors.note}>
              <Label tag="span" id="note">
                Message
              </Label>
              <WysiwygEditor
                ariaLabeledBy="note"
                handleInput={handleInput}
                value={data.note}
              />
            </Group>
            <Group
              error={errors.recipients}
              help="Separate multiple emails with comma"
            >
              <Label htmlFor="recipients" required>
                Recipients
              </Label>
              <TextArea
                height="short"
                id="recipients"
                onChange={handleInput}
                required
                value={data.recipients}
              />
            </Group>
            {hasServiceHistory && (
              <Row className="justify-start" stack={shouldStack}>
                <Group
                  className={!shouldStack && "wAuto"}
                  labelOffset={shouldStack ? "smaller" : "small"}
                >
                  <Checkbox
                    checked={data.serviceHistory}
                    color={colors.white}
                    id="service-history"
                    onChange={handleServiceHistory}
                  >
                    Include service history
                  </Checkbox>
                </Group>
                <Group className={!shouldStack && "wAuto"} labelOffset="small">
                  <Checkbox
                    checked={data.serviceNotes}
                    color={colors.white}
                    disabled={!data.serviceHistory}
                    id="service-notes"
                    onChange={handleServiceNotes}
                  >
                    Show notes
                  </Checkbox>
                </Group>
                <Group
                  className={!shouldStack && "wAuto"}
                  labelOffset={shouldStack ? "largeBottomOnly" : "small"}
                >
                  <Checkbox
                    checked={data.servicePrice}
                    color={colors.white}
                    disabled={!data.serviceHistory}
                    id="service-price"
                    onChange={handleServicePrice}
                  >
                    Show price
                  </Checkbox>
                </Group>
              </Row>
            )}
            <Group>
              <Label htmlFor="branding">HOLT Branding</Label>
              <Switch
                checked={data.branding}
                id="branding"
                onChange={handleBranding}
              />
            </Group>
            <Row stack={shouldStack}>
              <Group error={errors.first_name}>
                <Label htmlFor="first_name" required>
                  Your First Name
                </Label>
                <Text
                  id="first_name"
                  maxLength="30"
                  onChange={handleInput}
                  required
                  value={data.first_name}
                />
              </Group>
              <Group error={errors.last_name}>
                <Label htmlFor="last_name" required>
                  Your Last Name
                </Label>
                <Text
                  id="last_name"
                  maxLength="30"
                  onChange={handleInput}
                  required
                  value={data.last_name}
                />
              </Group>
            </Row>
            <Row stack={shouldStack}>
              <Group error={errors.phone}>
                <Label htmlFor="phone" required>
                  Your Phone Number
                </Label>
                <Text
                  id="phone"
                  maxLength="20"
                  onChange={handleInput}
                  required
                  type="tel"
                  value={data.phone}
                />
              </Group>
              <Group>
                <Label htmlFor="email" required>
                  Email
                </Label>
                <Text id="email" readOnly value={data.email} />
              </Group>
            </Row>
          </div>
          <div className="form-actions">
            <Button
              block={targetDevice === "mobile"}
              color="green400"
              disabled={inProgress}
              size="small"
              square={targetDevice === "mobile"}
              type="submit"
            >
              {inProgress ? "Sending…" : "Send"}
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
};

ModalSellSheet.propTypes = {
  categories: array,
  close: func.isRequired,
  files: arrayOf(
    shape({
      fileDate: oneOfType([object, string]),
      fileId: oneOfType([number, string]),
      fileIndex: number,
      fileIsPublic: bool,
      fileName: string,
      fileTitle: string,
      fileType: string,
    })
  ),
  listing: shape({
    equipmentId: oneOfType([number, string]),
  }),
  location: shape({
    state: shape({
      action: string,
      attachments: arrayOf(oneOfType([number, string])),
      email: string,
      hash: string,
      hideBranding: oneOfType([number, bool]),
      id: oneOfType([number, string]),
      includeServiceHistory: oneOfType([number, bool]),
      note: string,
      photos: arrayOf(string),
      price: oneOfType([number, string]),
      showServiceNotes: oneOfType([number, bool]),
      showServicePrice: oneOfType([number, bool]),
      user_id: oneOfType([number, string]),
    }),
  }),
  open: bool,
  targetDevice: string,
  trackEvent: func,
  trackModalView: func,
  updateUser: func.isRequired,
  user: shape({
    email: string,
    first_name: string,
    last_name: string,
    phone: string,
  }),
};

export default ModalSellSheet;
